import { useState, useEffect } from "react";

/**
 * CountdownTimer component
 * Displays a countdown timer based on expiry settings from privacy settings
 * @param {Date} acceptedAt - When the request was accepted
 * @param {Number} expiryValue - Expiry value (e.g., 1, 2, 3)
 * @param {String} expiryUnit - Expiry unit (hours, days, weeks, years)
 */
const CountdownTimer = ({ acceptedAt, expiryValue, expiryUnit }) => {
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    if (!acceptedAt || !expiryValue || !expiryUnit) {
      setTimeRemaining(null);
      return;
    }

    // Calculate expiry date from acceptance time
    const acceptedDate = new Date(acceptedAt);
    let milliseconds = 0;

    switch (expiryUnit) {
      case "minutes":
        milliseconds = expiryValue * 60 * 1000;
        break;
      case "hours":
        milliseconds = expiryValue * 60 * 60 * 1000;
        break;
      case "days":
        milliseconds = expiryValue * 24 * 60 * 60 * 1000;
        break;
      case "weeks":
        milliseconds = expiryValue * 7 * 24 * 60 * 60 * 1000;
        break;
      case "months":
        milliseconds = expiryValue * 30 * 24 * 60 * 60 * 1000; // Approximate: 30 days per month
        break;
      case "years":
        milliseconds = expiryValue * 365 * 24 * 60 * 60 * 1000;
        break;
      default:
        milliseconds = 0;
    }

    const expiryDate = new Date(acceptedDate.getTime() + milliseconds);

    const updateTimer = () => {
      const now = new Date();
      const diff = expiryDate - now;

      if (diff <= 0) {
        setTimeRemaining("Expired");
        return;
      }

      // Calculate time components
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      // Format display
      let display = "";
      if (days > 0) {
        display = `${days}d ${hours}h ${minutes}m`;
      } else if (hours > 0) {
        display = `${hours}h ${minutes}m ${seconds}s`;
      } else if (minutes > 0) {
        display = `${minutes}m ${seconds}s`;
      } else {
        display = `${seconds}s`;
      }

      setTimeRemaining(display);
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [acceptedAt, expiryValue, expiryUnit]);

  if (!timeRemaining) {
    return null;
  }

  return (
    <div className="flex items-center justify-end">
      {timeRemaining === "Expired" ? (
        <span className="px-3 py-1 text-xs font-semibold text-action bg-action/10 rounded-full whitespace-nowrap">
          Access Expired
        </span>
      ) : (
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-action"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm font-medium text-action">
            Expires in: {timeRemaining}
          </span>
        </div>
      )}
    </div>
  );
};

export default CountdownTimer;

